import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { organizationsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Building2, 
  Plus, 
  Search,
  Loader2,
  Edit2,
  Trash2,
  Users,
  FileText,
  CreditCard
} from 'lucide-react';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';

export default function OrganizationsPage() {
  const { user, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    if (!isSuperAdmin) {
      navigate('/dashboard');
      return;
    }
    fetchOrganizations();
  }, [isSuperAdmin, navigate]);

  const fetchOrganizations = async () => {
    try {
      const response = await organizationsAPI.getAll();
      setOrganizations(response.data);
    } catch (error) {
      toast.error('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await organizationsAPI.delete(deleteId);
      setOrganizations(organizations.filter(o => o.id !== deleteId));
      toast.success('Organization deleted successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete organization');
    } finally {
      setDeleteId(null);
    }
  };

  const filteredOrgs = organizations.filter(o => 
    o.name.toLowerCase().includes(search.toLowerCase()) ||
    o.contact_email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />

      {/* Main Content */}
      <main className="ml-0 md:ml-64 p-4 md:p-8 pt-16 md:pt-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground tracking-tight">
              Organizations
            </h1>
            <p className="font-body text-muted-foreground">
              Manage client company portals
            </p>
          </div>
          <Link to="/admin/organizations/new">
            <Button className="btn-lift bg-primary text-white font-body" data-testid="add-org-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Organization
            </Button>
          </Link>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 font-body bg-card"
              data-testid="search-orgs-input"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : filteredOrgs.length === 0 ? (
            <div className="text-center py-16">
              <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
              <p className="font-body text-muted-foreground mb-4">No organizations found</p>
              <Link to="/admin/organizations/new">
                <Button className="btn-lift bg-primary text-white font-body" data-testid="add-first-org-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Organization
                </Button>
              </Link>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/50">
                  <TableHead className="font-heading font-semibold">Organization</TableHead>
                  <TableHead className="font-heading font-semibold">Contact</TableHead>
                  <TableHead className="font-heading font-semibold">Cases</TableHead>
                  <TableHead className="font-heading font-semibold">Users</TableHead>
                  <TableHead className="font-heading font-semibold">Status</TableHead>
                  <TableHead className="font-heading font-semibold">Payments</TableHead>
                  <TableHead className="font-heading font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrgs.map((org) => (
                  <TableRow key={org.id} className="hover:bg-secondary/30 transition-colors" data-testid={`org-row-${org.id}`}>
                    <TableCell className="font-body font-medium">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-primary" strokeWidth={1.5} />
                        </div>
                        <div>
                          <p className="font-medium">{org.name}</p>
                          <p className="text-xs text-muted-foreground">
                            Created {new Date(org.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="font-body text-muted-foreground">
                      <div>
                        <p>{org.contact_email || '-'}</p>
                        <p className="text-xs">{org.contact_phone || ''}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-muted-foreground" />
                        <span className="font-body">{org.case_count}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-muted-foreground" />
                        <span className="font-body">{org.user_count}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-body font-medium ${
                        org.is_active ? 'bg-success text-white' : 'bg-muted text-muted-foreground'
                      }`}>
                        {org.is_active ? 'Active' : 'Disabled'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <CreditCard className={`w-3.5 h-3.5 ${org.stripe_connect_status === 'active' ? 'text-green-600' : 'text-muted-foreground'}`} />
                        <span className={`text-xs font-body font-medium ${
                          org.stripe_connect_status === 'active' ? 'text-green-700' : 
                          org.stripe_connect_status === 'pending' || org.stripe_connect_status === 'pending_verification' ? 'text-amber-600' : 'text-muted-foreground'
                        }`}>
                          {org.stripe_connect_status === 'active' ? 'Connected' : 
                           org.stripe_connect_status === 'pending' || org.stripe_connect_status === 'pending_verification' ? 'Pending' :
                           org.stripe_connect_status === 'onboarding_incomplete' ? 'Incomplete' : 'Not Set Up'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link to={`/admin/organizations/${org.id}/edit`}>
                          <Button variant="ghost" size="sm" className="text-primary" data-testid={`edit-org-${org.id}`}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        </Link>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-error hover:text-error"
                          onClick={() => setDeleteId(org.id)}
                          data-testid={`delete-org-${org.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete Organization</AlertDialogTitle>
            <AlertDialogDescription className="font-body">
              Are you sure you want to delete this organization? This will also delete all associated cases, documents, and users. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-body" data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete} 
              className="bg-error text-white hover:bg-error/90 font-body"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
